import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, Award, Building2, CheckCircle2, ChevronRight, Download, EyeOff, FlaskConical, Home, IndianRupee,
  Landmark, Loader2, Lock, MapPin, MessageCircle, Phone, Printer, RefreshCw, Search, ShieldCheck, Smartphone, User, Wallet,
} from "lucide-react";
import { CertificateVerifyLink, PublicCertificateDownloadButton, originParam } from "../../components/TransferCertificate";  // Phase 124
import { TaxHeadPicker, initialSelection, summarizeSelection } from "../../components/TaxHeadPicker";   // Phase 229
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, downloadBlob, errMsg, fmtDate, fmtINR } from "../../lib/api";

const SEARCH_MODES = [
  { value: "pin", label: "Property Number", placeholder: "e.g. BMC100041", hint: "The PIN / property number printed on your demand notice", icon: Landmark },
  { value: "house_no", label: "House Number", placeholder: "e.g. 12/A", hint: "House number as recorded by the municipality", icon: Home },
  { value: "mobile", label: "Mobile Number", placeholder: "10-digit mobile, or last 4 digits", hint: "Mobile number registered against the property", icon: Smartphone },
  { value: "owner_name", label: "Owner Name", placeholder: "e.g. Ramesh Kumar", hint: "Full or partial name of the owner", icon: User },
];
const STEPS = [
  { key: "search", label: "Find property" },
  { key: "property", label: "Dues & amount" },
  { key: "pay", label: "Pay" },
  { key: "done", label: "Receipt" },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

/** Public top bar — municipality identity + secondary staff / citizen login links. */
export function PortalHeader({ muni, code }) {
  return (
    <header className="border-b bg-card">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
        <Link to={code ? `/${code}/pay` : "/pay"} className="flex items-center gap-2 min-w-0" data-testid="portal-home-link">
          <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center shrink-0">
            <Building2 className="h-5 w-5 text-primary-foreground" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold font-heading leading-tight truncate" data-testid="portal-muni-name">{muni?.name || "MunicipalConnect"}</p>
            <p className="text-[11px] text-muted-foreground leading-tight">Citizen Payment Portal · no login needed</p>
          </div>
        </Link>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="ghost" size="sm" asChild className="hidden sm:inline-flex" data-testid="portal-staff-login-link">
            <Link to="/login">Staff login</Link>
          </Button>
          <Button variant="outline" size="sm" asChild data-testid="portal-citizen-login-link">
            <Link to={code ? `/citizen?muni=${code}` : "/citizen"}>Citizen login</Link>
          </Button>
        </div>
      </div>
    </header>
  );
}

function Stepper({ current }) {
  const idx = STEPS.findIndex((s) => s.key === current);
  return (
    <ol className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm" data-testid="portal-stepper" aria-label="Payment steps">
      {STEPS.map((s, i) => {
        const state = i < idx ? "done" : i === idx ? "active" : "todo";
        return (
          <li key={s.key} className="flex items-center gap-1 sm:gap-2" data-testid={`portal-step-${s.key}`} data-state={state}>
            <span className={`h-6 w-6 rounded-full inline-flex items-center justify-center text-[11px] font-semibold border transition-colors duration-200
              ${state === "done" ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] border-transparent"
                : state === "active" ? "bg-primary text-primary-foreground border-transparent" : "bg-card text-muted-foreground border-border"}`}>
              {state === "done" ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
            </span>
            <span className={`${state === "active" ? "font-medium text-foreground" : "text-muted-foreground"} hidden xs:inline sm:inline`}>{s.label}</span>
            {i < STEPS.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60" aria-hidden="true" />}
          </li>
        );
      })}
    </ol>
  );
}

function DueBadge({ row }) {
  if (!row.has_demand) return <Badge variant="outline" className="text-muted-foreground" data-testid="due-badge-none">No demand</Badge>;
  if (row.balance_due <= 0) return <Badge className="bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border-[hsl(var(--success))]/20" variant="outline" data-testid="due-badge-paid">Fully paid</Badge>;
  if (row.total_collected > 0) return <Badge className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20" variant="outline" data-testid="due-badge-partial">Partly paid</Badge>;
  return <Badge className="bg-destructive/10 text-destructive border-destructive/20" variant="outline" data-testid="due-badge-pending">Due</Badge>;
}

// ------------------------------------------------------------------------------------------------
// Step 1 — search
// ------------------------------------------------------------------------------------------------
function SearchStep({ code, config, onPick, initial }) {
  const [by, setBy] = useState(initial?.by || "pin");
  const [q, setQ] = useState(initial?.q || "");
  const [zoneId, setZoneId] = useState(initial?.zone_id || "");
  const [wardId, setWardId] = useState(initial?.ward_id || "");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mode = SEARCH_MODES.find((m) => m.value === by) || SEARCH_MODES[0];
  const wards = useMemo(() => (config?.wards || []).filter((w) => !zoneId || w.zone_id === zoneId), [config, zoneId]);

  const run = useCallback(async (e) => {
    e?.preventDefault();
    if (!q.trim()) { setError("Type what you want to search for"); return; }
    setLoading(true); setError("");
    try {
      const r = await api.get(`/t/${code}/public/pay/search`, { params: { by, q: q.trim(), zone_id: zoneId || undefined, ward_id: wardId || undefined } });
      setResult(r.data);
    } catch (err) {
      setResult(null); setError(errMsg(err));
    } finally { setLoading(false); }
  }, [code, by, q, zoneId, wardId]);

  useEffect(() => { if (initial?.q) run(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-4">
      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={run} className="space-y-4" data-testid="portal-search-form">
            <div>
              <Label className="text-xs text-muted-foreground">Search by</Label>
              <RadioGroup value={by} onValueChange={(v) => { setBy(v); setResult(null); setError(""); }}
                className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1.5" data-testid="portal-search-by">
                {SEARCH_MODES.map((m) => (
                  <Label key={m.value} htmlFor={`by-${m.value}`}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 cursor-pointer text-sm transition-colors duration-150 min-h-[44px]
                      ${by === m.value ? "border-primary bg-primary/5 text-foreground" : "border-border hover:bg-muted/60 text-muted-foreground"}`}
                    data-testid={`portal-search-by-${m.value}`}>
                    <RadioGroupItem value={m.value} id={`by-${m.value}`} className="sr-only" />
                    <m.icon className="h-4 w-4 shrink-0" />
                    <span className="leading-tight">{m.label}</span>
                  </Label>
                ))}
              </RadioGroup>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px_auto] gap-3 items-end">
              <div className="space-y-1">
                <Label htmlFor="portal-q" className="text-xs">{mode.label}</Label>
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input id="portal-q" className="pl-9 h-11 text-base" placeholder={mode.placeholder} value={q}
                    inputMode={by === "mobile" ? "numeric" : "text"} autoComplete="off"
                    onChange={(e) => setQ(e.target.value)} data-testid="citizen-property-search-input" />
                </div>
                <p className="text-[11px] text-muted-foreground">{mode.hint}</p>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Zone <span className="text-muted-foreground">(optional)</span></Label>
                <Select value={zoneId || "all"} onValueChange={(v) => { setZoneId(v === "all" ? "" : v); setWardId(""); }}>
                  <SelectTrigger className="h-11" data-testid="portal-zone-select"><SelectValue placeholder="All zones" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All zones</SelectItem>
                    {(config?.zones || []).map((z) => <SelectItem key={z.id} value={z.id} data-testid={`portal-zone-option-${z.id}`}>{z.zone_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Ward <span className="text-muted-foreground">(optional)</span></Label>
                <Select value={wardId || "all"} onValueChange={(v) => setWardId(v === "all" ? "" : v)}>
                  <SelectTrigger className="h-11" data-testid="portal-ward-select"><SelectValue placeholder="All wards" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All wards</SelectItem>
                    {wards.map((w) => <SelectItem key={w.id} value={w.id} data-testid={`portal-ward-option-${w.id}`}>{w.ward_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" size="lg" className="h-11 w-full md:w-auto" disabled={loading} data-testid="portal-search-button">
                {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />} Search
              </Button>
            </div>
            {error && <p className="text-sm text-destructive" role="alert" data-testid="portal-search-error">{error}</p>}
          </form>
        </CardContent>
      </Card>

      {loading && !result && (
        <div className="space-y-2" data-testid="portal-search-skeleton">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      )}

      {result && (
        <div className="space-y-2" data-testid="portal-search-results">
          <p className="text-sm text-muted-foreground px-1" data-testid="portal-search-count">
            {result.total === 0 ? "No property matched your search." : `${result.total} propert${result.total === 1 ? "y" : "ies"} found`}
            {result.truncated && <span> · showing the first {result.items.length} — add a Zone / Ward or type more characters to narrow down</span>}
          </p>
          {result.total === 0 && (
            <Card className="shadow-sm"><CardContent className="p-6 text-center">
              <p className="font-medium">Nothing found</p>
              <p className="text-sm text-muted-foreground mt-1">Check the spelling, try another search option (mobile number or owner name), or remove the Zone / Ward filter.</p>
            </CardContent></Card>
          )}
          {result.items.map((p) => (
            <Card key={p.id} className="shadow-sm hover:shadow-md transition-shadow duration-200" data-testid={`portal-result-${p.pin}`}>
              <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono font-semibold" data-testid={`portal-result-pin-${p.pin}`}>{p.pin}</span>
                    <DueBadge row={p} />
                  </div>
                  <p className="text-sm font-medium mt-1 truncate">{p.owner_name || "Owner name not recorded"}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {[p.house_no && `H.No ${p.house_no}`, p.address].filter(Boolean).join(" · ") || "Address not recorded"}
                  </p>
                  <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-0.5">
                    <MapPin className="h-3 w-3" /> {[p.zone_label, p.ward_label].filter(Boolean).join(" · ") || "Territory not set"}
                    {p.mobile_masked && <span> · <Phone className="h-3 w-3 inline" /> {p.mobile_masked}</span>}
                  </p>
                </div>
                <div className="flex items-center justify-between sm:flex-col sm:items-end gap-2 shrink-0">
                  <div className="text-left sm:text-right">
                    <p className="text-[11px] text-muted-foreground">Balance due</p>
                    <p className={`text-lg font-semibold tabular-nums leading-tight ${p.balance_due > 0 ? "text-destructive" : "text-[hsl(var(--success))]"}`}
                      data-testid={`portal-result-balance-${p.pin}`}>{fmtINR(p.balance_due)}</p>
                  </div>
                  <Button size="sm" className="h-10" onClick={() => onPick(p, { by, q, zone_id: zoneId, ward_id: wardId })} data-testid={`portal-result-view-${p.pin}`}>
                    {p.balance_due > 0 ? "View & pay" : "View details"} <ArrowRight className="h-4 w-4 ml-1.5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------
// Step 2 — property dues + amount + payer details
// ------------------------------------------------------------------------------------------------
function Kpi({ label, value, tone, testId }) {
  const toneCls = tone === "danger" ? "text-destructive" : tone === "success" ? "text-[hsl(var(--success))]" : "text-foreground";
  return (
    <Card className="shadow-sm"><CardContent className="p-3 sm:p-4">
      <p className="text-[11px] sm:text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg sm:text-2xl font-semibold tabular-nums leading-tight mt-0.5 ${toneCls}`} data-testid={testId}>{fmtINR(value)}</p>
    </CardContent></Card>
  );
}

function PropertyStep({ code, propId, onBack, onProceed, gatewayMode }) {
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");
  const [payType, setPayType] = useState("full");
  const [amount, setAmount] = useState("");
  const [payer, setPayer] = useState({ name: "", mobile: "", email: "" });
  const [busy, setBusy] = useState(false);
  // Phase 229: dues under several tax heads -> the payer ticks the head(s) / year(s) and an amount for each
  // (everything pre-ticked in full); only those demands are settled. One head -> full / part amount as before.
  const [headSel, setHeadSel] = useState({});

  const load = useCallback(() => {
    setErr("");
    api.get(`/t/${code}/public/pay/property/${propId}`).then((r) => {
      setP(r.data);
      setPayer((s) => ({ ...s, name: s.name || r.data.owner_name || "" }));
      const openLines = (r.data.demands || []).filter((d) => d.balance_amount > 0);
      setHeadSel(r.data.multi_head ? initialSelection(openLines) : {});
    }).catch((e) => setErr(errMsg(e)));
  }, [code, propId]);
  useEffect(() => { load(); }, [load]);

  const headsMode = !!p?.multi_head;
  const headLines = useMemo(() => (p?.demands || []).filter((d) => d.balance_amount > 0), [p]);
  const headSummary = useMemo(() => summarizeSelection(headLines, headSel), [headLines, headSel]);
  const payable = p?.payable_now || 0;
  const amt = headsMode ? headSummary.total : payType === "full" ? payable : Number(amount || 0);
  const amtError = payType === "partial" && !headsMode
    ? (!amount ? "" : amt < (p?.min_amount || 1) ? `Minimum ₹${p?.min_amount || 1}` : amt > payable + 0.005 ? `Cannot exceed the balance due (${fmtINR(payable)})` : "")
    : "";
  const headsOk = !headsMode || (headSummary.errors.length === 0 && headSummary.selectedCount > 0);
  const mobileDigits = payer.mobile.replace(/\D/g, "");
  const canPay = p && payable > 0 && headsOk && amt >= (p.min_amount || 1) && !amtError && payer.name.trim().length >= 2 && mobileDigits.length === 10;

  const proceed = async () => {
    if (!canPay) return;
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/public/pay/order`, {
        property_id: p.id, amount: Number(amt.toFixed(2)), payer_name: payer.name.trim(), payer_mobile: mobileDigits,
        payer_email: payer.email.trim() || null,
        ...(headsMode ? { allocations: headSummary.allocations } : {}),   // Phase 229
      });
      onProceed({ order: r.data, property: p, payer: { ...payer, mobile: mobileDigits } });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (err) {
    return (
      <Card className="shadow-sm"><CardContent className="p-6 text-center space-y-3" data-testid="portal-property-error">
        <p className="text-sm text-destructive">{err}</p>
        <div className="flex justify-center gap-2">
          <Button variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to search</Button>
          <Button onClick={load}><RefreshCw className="h-4 w-4 mr-1.5" /> Retry</Button>
        </div>
      </CardContent></Card>
    );
  }
  if (!p) {
    return <div className="space-y-3" data-testid="portal-property-skeleton"><Skeleton className="h-28 w-full rounded-xl" /><Skeleton className="h-20 w-full rounded-xl" /><Skeleton className="h-40 w-full rounded-xl" /></div>;
  }

  return (
    <div className="space-y-4 pb-24 md:pb-0" data-testid="portal-property-step">
      <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2 text-muted-foreground" data-testid="portal-back-to-search">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to results
      </Button>

      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs text-muted-foreground">Property</p>
              <p className="font-mono text-xl font-semibold" data-testid="portal-property-pin">{p.pin}</p>
              <p className="text-base font-medium mt-1" data-testid="portal-property-owner">{p.owner_name || "Owner name not recorded"}</p>
              {p.father_spouse_name && <p className="text-xs text-muted-foreground">S/o, D/o, W/o {p.father_spouse_name}</p>}
              <p className="text-sm text-muted-foreground mt-1" data-testid="portal-property-address">
                {[p.house_no && `H.No ${p.house_no}`, p.address].filter(Boolean).join(", ") || "Address not recorded"}
              </p>
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1 mt-1" data-testid="portal-property-territory">
                <MapPin className="h-3 w-3" /> {[p.zone_label, p.ward_label].filter(Boolean).join(" · ") || "Territory not set"}
                {p.mobile_masked && <span> · registered mobile {p.mobile_masked}</span>}
              </p>
            </div>
            <DueBadge row={p} />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="portal-property-kpis">
        <Kpi label="Total demand" value={p.total_demand} testId="portal-kpi-demand" />
        <Kpi label="Already paid" value={p.total_collected} tone="success" testId="portal-kpi-paid" />
        <Kpi label="Balance due" value={p.balance_due} tone={p.balance_due > 0 ? "danger" : "success"} testId="portal-kpi-balance" />
      </div>
      {p.advance_credit > 0 && <p className="text-xs text-muted-foreground px-1">Advance credit on this property: {fmtINR(p.advance_credit)}</p>}

      {p.ownership_certificate && (
        <Card className="shadow-sm border-primary/20" data-testid="portal-ownership-certificate">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex items-start gap-3">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Award className="h-5 w-5 text-primary" /></div>
              <div className="min-w-0">
                <p className="text-sm font-medium">Ownership transfer certificate</p>
                <p className="text-xs text-muted-foreground">
                  <span className="font-mono" data-testid="portal-ownership-certificate-number">{p.ownership_certificate.certificate_number}</span>
                  {" • "}{p.ownership_certificate.old_owner_name} → <span className="font-medium text-foreground">{p.ownership_certificate.new_owner_name}</span>
                  {p.ownership_certificate.approved_date && ` • approved ${p.ownership_certificate.approved_date}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <CertificateVerifyLink code={code} token={p.ownership_certificate.token} testId="portal-ownership-certificate-verify" />
              <PublicCertificateDownloadButton code={code} token={p.ownership_certificate.token} certificateNumber={p.ownership_certificate.certificate_number}
                variant="outline" testId="portal-ownership-certificate-download" />
            </div>
          </CardContent>
        </Card>
      )}

      {p.demands?.length > 0 && (
        <Card className="shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table data-testid="portal-demand-table">
                <TableHeader><TableRow>
                  <TableHead>Tax head</TableHead><TableHead className="hidden sm:table-cell">Year</TableHead>
                  <TableHead className="text-right">Demand</TableHead><TableHead className="text-right hidden sm:table-cell">Paid</TableHead>
                  <TableHead className="text-right">Balance</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {p.demands.map((d) => (
                    <TableRow key={d.demand_id} data-testid={`portal-demand-row-${d.demand_id}`}>
                      <TableCell className="text-sm">{d.tax_head}<span className="sm:hidden text-muted-foreground text-xs"> · {d.fy_label || "-"}</span>
                        {d.penalty_new > 0 && <p className="text-[11px] text-[hsl(var(--warning))]">+ {fmtINR(d.penalty_new)} penalty applies today</p>}
                        {d.discount > 0 && <p className="text-[11px] text-[hsl(var(--success))]">− {fmtINR(d.discount)} discount on full payment</p>}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell text-sm">{d.fy_label || "-"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm">{fmtINR(d.total_demand)}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm hidden sm:table-cell">{fmtINR(d.paid_amount)}</TableCell>
                      <TableCell className={`text-right tabular-nums text-sm font-medium ${d.balance_amount > 0 ? "text-destructive" : "text-muted-foreground"}`}>{fmtINR(d.balance_amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {p.responsible?.staff?.length > 0 && (
        <p className="text-xs text-muted-foreground px-1 inline-flex items-center gap-1" data-testid="portal-responsible">
          <ShieldCheck className="h-3.5 w-3.5" /> {p.responsible.type === "collector" ? "Your tax collector" : "Zone supervisor"}: {p.responsible.staff.map((s) => s.name).join(", ")}
        </p>
      )}

      {payable <= 0 ? (
        <Card className="shadow-sm border-[hsl(var(--success))]/30"><CardContent className="p-6 text-center" data-testid="portal-nothing-due">
          <CheckCircle2 className="h-8 w-8 mx-auto text-[hsl(var(--success))]" />
          <p className="font-medium mt-2">No dues pending on this property</p>
          <p className="text-sm text-muted-foreground mt-1">{p.has_demand ? "Everything raised so far has been paid. Thank you!" : "No tax demand has been raised for this property yet."}</p>
        </CardContent></Card>
      ) : (
        <Card className="shadow-sm" data-testid="portal-pay-card">
          <CardContent className="p-4 sm:p-6 space-y-5">
            <div>
              <p className="font-semibold font-heading inline-flex items-center gap-2"><Wallet className="h-4 w-4" /> {headsMode ? "Which tax heads are you paying?" : "How much would you like to pay?"}</p>
              {p.quote?.penalty > 0 && <p className="text-xs text-[hsl(var(--warning))] mt-1">Includes {fmtINR(p.quote.penalty)} penalty applicable as of today.</p>}
              {p.quote?.discount_on_full_payment > 0 && <p className="text-xs text-[hsl(var(--success))] mt-1">Pay in full today and save {fmtINR(p.quote.discount_on_full_payment)} (early-payment discount).</p>}
            </div>
            {headsMode ? (
              <TaxHeadPicker lines={headLines} value={headSel} onChange={setHeadSel} disabled={busy} testIdPrefix="portal-heads" />
            ) : (
            <>
            <RadioGroup value={payType} onValueChange={setPayType} className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="portal-pay-type">
              <Label htmlFor="pay-full" className={`rounded-lg border p-3 cursor-pointer transition-colors duration-150 ${payType === "full" ? "border-primary bg-primary/5" : "hover:bg-muted/60"}`} data-testid="portal-pay-type-full">
                <span className="flex items-center gap-2"><RadioGroupItem value="full" id="pay-full" /><span className="font-medium text-sm">Pay full balance</span></span>
                <span className="block text-xl font-semibold tabular-nums mt-1 pl-6" data-testid="portal-pay-full-amount">{fmtINR(payable)}</span>
              </Label>
              <Label htmlFor="pay-partial" className={`rounded-lg border p-3 cursor-pointer transition-colors duration-150 ${payType === "partial" ? "border-primary bg-primary/5" : "hover:bg-muted/60"}`} data-testid="portal-pay-type-partial">
                <span className="flex items-center gap-2"><RadioGroupItem value="partial" id="pay-partial" /><span className="font-medium text-sm">Pay a part now</span></span>
                <span className="block text-xs text-muted-foreground mt-1 pl-6">Any amount from ₹{p.min_amount || 1} up to {fmtINR(payable)}</span>
              </Label>
            </RadioGroup>
            {payType === "partial" && (
              <div className="space-y-1 max-w-xs">
                <Label htmlFor="partial-amount" className="text-xs">Amount to pay (₹)</Label>
                <div className="relative">
                  <IndianRupee className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                  <Input id="partial-amount" type="number" inputMode="decimal" min={p.min_amount || 1} max={payable} step="1" className={`pl-9 h-11 text-base ${amtError ? "border-destructive" : ""}`}
                    value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`1 – ${payable}`} data-testid="portal-partial-amount" aria-invalid={!!amtError} />
                </div>
                {amtError && <p className="text-xs text-destructive" data-testid="portal-partial-amount-error">{amtError}</p>}
              </div>
            )}
            </>
            )}

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-medium inline-flex items-center gap-2"><MessageCircle className="h-4 w-4" /> Where should we send the receipt?</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label htmlFor="payer-name" className="text-xs">Your name</Label>
                  <Input id="payer-name" className="h-11" value={payer.name} onChange={(e) => setPayer({ ...payer, name: e.target.value })} placeholder="Name of the person paying" data-testid="portal-payer-name" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="payer-mobile" className="text-xs">Mobile number <span className="text-destructive">*</span></Label>
                  <Input id="payer-mobile" className="h-11" inputMode="numeric" maxLength={13} value={payer.mobile}
                    onChange={(e) => setPayer({ ...payer, mobile: e.target.value })} placeholder="10-digit mobile" data-testid="portal-payer-mobile"
                    aria-invalid={payer.mobile.length > 0 && mobileDigits.length !== 10} />
                  <p className="text-[11px] text-muted-foreground">SMS + WhatsApp confirmation goes here</p>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="payer-email" className="text-xs">Email <span className="text-muted-foreground">(optional)</span></Label>
                  <Input id="payer-email" type="email" className="h-11" value={payer.email} onChange={(e) => setPayer({ ...payer, email: e.target.value })} placeholder="you@example.com" data-testid="portal-payer-email" />
                </div>
              </div>
            </div>

            {/* desktop CTA; mobile uses the sticky bar below */}
            <div className="hidden md:flex items-center justify-between gap-3 pt-2">
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5" /> {gatewayMode === "simulated" ? "Test gateway — no money is charged in this environment" : "Secured by Razorpay · UPI, cards, net banking"}
              </p>
              <Button size="lg" className="h-11" disabled={!canPay || busy} onClick={proceed} data-testid="citizen-pay-now-button">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />} Proceed to pay {amt > 0 ? fmtINR(amt) : ""}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {payable > 0 && (
        <div className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t bg-card/95 backdrop-blur px-4 py-3 flex items-center justify-between gap-3" data-testid="portal-mobile-paybar">
          <div>
            <p className="text-[11px] text-muted-foreground">You pay</p>
            <p className="text-lg font-semibold tabular-nums leading-tight">{amt > 0 ? fmtINR(amt) : "—"}</p>
          </div>
          <Button size="lg" className="h-12 flex-1 max-w-[60%]" disabled={!canPay || busy} onClick={proceed} data-testid="citizen-pay-now-button-mobile">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />} Proceed to pay
          </Button>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------
// Step 3 — gateway (simulated dialog or Razorpay checkout)
// ------------------------------------------------------------------------------------------------
function PayStep({ code, order, property, payer, onDone, onBack }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState("");
  const startedRef = useRef(false);

  const confirm = useCallback(async (gw = {}) => {
    setBusy(true); setFailed("");
    try {
      const r = await api.post(`/t/${code}/public/pay/confirm`, { order_id: order.order_id, origin: window.location.origin, ...gw });
      onDone(r.data);
    } catch (e) {
      setFailed(errMsg(e)); toast.error(errMsg(e));
    } finally { setBusy(false); }
  }, [code, order, onDone]);

  const openRazorpay = useCallback(async () => {
    const ok = await loadRazorpayScript();
    if (!ok) { setFailed("Could not load the payment gateway. Check your internet connection and try again."); return; }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency, name: order.name, description: order.description,
      order_id: order.order_id, prefill: order.prefill, theme: { color: "#0b4f86" },
      handler: (resp) => confirm({ razorpay_payment_id: resp.razorpay_payment_id, razorpay_signature: resp.razorpay_signature }),
      modal: { ondismiss: () => setFailed("Payment was cancelled before completion. You can try again.") },
    });
    rzp.open();
  }, [order, confirm]);

  useEffect(() => {
    if (order.mode === "razorpay" && !startedRef.current) { startedRef.current = true; openRazorpay(); }
  }, [order, openRazorpay]);

  return (
    <div className="space-y-4" data-testid="portal-pay-step">
      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs text-muted-foreground">Paying for</p>
              <p className="font-mono font-semibold">{property.pin}</p>
              <p className="text-sm">{property.owner_name}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Amount</p>
              <p className="text-2xl font-semibold tabular-nums" data-testid="portal-pay-amount">{fmtINR(order.amount_inr)}</p>
              {order.is_partial && <Badge variant="outline" className="mt-1 bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20">Partial payment</Badge>}
            </div>
          </div>
          <div className="rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground grid grid-cols-1 sm:grid-cols-2 gap-1">
            <span>Payer: <span className="text-foreground">{payer.name}</span></span>
            <span>Receipt to: <span className="text-foreground">+91 {payer.mobile}</span> (SMS + WhatsApp)</span>
            <span className="sm:col-span-2 font-mono">Order {order.order_id}</span>
          </div>
          {failed && <p className="text-sm text-destructive" role="alert" data-testid="portal-pay-error">{failed}</p>}
          <div className="flex flex-wrap items-center gap-2 justify-between">
            <Button variant="ghost" onClick={onBack} disabled={busy} data-testid="portal-pay-back"><ArrowLeft className="h-4 w-4 mr-1.5" /> Change amount</Button>
            {order.mode === "razorpay" ? (
              <Button size="lg" className="h-11" onClick={openRazorpay} disabled={busy} data-testid="portal-open-gateway-button">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Lock className="h-4 w-4 mr-2" />} {failed ? "Try again" : "Open secure payment"}
              </Button>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {/* SIMULATED gateway — stands in for the Razorpay checkout until keys are configured */}
      <Dialog open={order.mode === "simulated"} onOpenChange={(o) => { if (!o && !busy) onBack(); }}>
        <DialogContent className="sm:max-w-md" data-testid="portal-simulated-gateway">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock className="h-4 w-4" /> Secure payment</DialogTitle>
            <DialogDescription>
              <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20 mr-1.5"><FlaskConical className="h-3 w-3 mr-1" /> Test mode</Badge>
              This municipality has not enabled a live payment gateway yet. Clicking Pay records the payment instantly — <span className="font-medium text-foreground">no money is charged</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border p-4 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Merchant</span><span className="font-medium text-right">{order.name}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Property</span><span className="font-mono">{property.pin}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Amount</span><span className="text-lg font-semibold tabular-nums" data-testid="portal-simulated-amount">{fmtINR(order.amount_inr)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Order</span><span className="font-mono text-xs">{order.order_id}</span></div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={onBack} disabled={busy} data-testid="portal-simulated-cancel">Cancel</Button>
            <Button className="h-11" onClick={() => confirm()} disabled={busy} data-testid="portal-simulated-pay-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <IndianRupee className="h-4 w-4 mr-2" />} Pay {fmtINR(order.amount_inr)} now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ------------------------------------------------------------------------------------------------
// Step 4 — success
// ------------------------------------------------------------------------------------------------
export function NotificationStatus({ code, token, initial }) {
  const [rows, setRows] = useState(initial || null);
  useEffect(() => {
    if (initial) return;
    let tries = 0;
    const tick = () => api.get(`/t/${code}/public/pay/receipt/${token}`).then((r) => {
      setRows(r.data.notifications || []);
      if ((r.data.notifications || []).length === 0 && tries++ < 4) setTimeout(tick, 1200);
    }).catch(() => {});
    const t = setTimeout(tick, 800);   // the SMS / WhatsApp dispatch is fire-and-forget on the server
    return () => clearTimeout(t);
  }, [code, token, initial]);
  const label = (s) => s === "sent" ? "sent" : s === "simulated" ? "queued (test mode)" : s === "failed" ? "failed" : s;
  if (!rows) return <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="portal-notify-status-loading"><Loader2 className="h-3 w-3 animate-spin" /> Sending SMS and WhatsApp confirmation…</p>;
  if (rows.length === 0) return <p className="text-xs text-muted-foreground" data-testid="portal-notify-status-none">Confirmation message is being prepared — it will reach your mobile shortly.</p>;
  return (
    <ul className="text-xs text-muted-foreground space-y-0.5" data-testid="portal-notify-status">
      {rows.map((r, i) => (
        <li key={i} className="flex items-center gap-1.5 flex-wrap" data-testid={`portal-notify-${r.channel}`} data-status={r.status}>
          {r.channel === "whatsapp" ? <MessageCircle className="h-3.5 w-3.5 shrink-0" /> : <Smartphone className="h-3.5 w-3.5 shrink-0" />}
          <span><span className="capitalize">{r.channel === "sms" ? "SMS" : r.channel}</span> to {r.to}: <span className={r.status === "failed" ? "text-destructive" : "text-foreground font-medium"}>{label(r.status)}</span></span>
        </li>
      ))}
    </ul>
  );
}

function DoneStep({ code, result, onNew }) {
  const [downloading, setDownloading] = useState(false);
  const r = result.receipt || {};
  const download = async () => {
    setDownloading(true);
    try {
      await downloadBlob(`/t/${code}/public/pay/receipt/${result.token}/pdf?${originParam()}`, `receipt-${result.receipt_number}.pdf`);
      toast.success("Receipt downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setDownloading(false); }
  };
  return (
    <div className="space-y-4" data-testid="portal-done-step">
      <Card className="shadow-sm border-[hsl(var(--success))]/30">
        <CardContent className="p-6 text-center space-y-2">
          <div className="h-14 w-14 mx-auto rounded-full bg-[hsl(var(--success))]/10 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-[hsl(var(--success))]" />
          </div>
          <p className="text-xl font-semibold font-heading" data-testid="portal-success-title">Payment successful</p>
          <p className="text-sm text-muted-foreground">Thank you. Your payment has been recorded with the municipality.</p>
          <p className="text-3xl font-semibold tabular-nums pt-1" data-testid="portal-success-amount">{fmtINR(result.amount)}</p>
          <p className="text-sm">Receipt No. <span className="font-mono font-semibold" data-testid="portal-success-receipt">{result.receipt_number}</span></p>
          {result.gateway_mode === "simulated" && (
            <Badge variant="outline" className="bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/20" data-testid="portal-success-simulated">
              <FlaskConical className="h-3 w-3 mr-1" /> Test payment — no money was charged
            </Badge>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm">
        <CardContent className="p-4 sm:p-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
          <div><p className="text-xs text-muted-foreground">Property</p><p className="font-mono font-semibold">{r.pin}</p></div>
          <div><p className="text-xs text-muted-foreground">Owner</p><p className="font-medium">{r.owner_name || "-"}</p></div>
          <div className="col-span-2"><p className="text-xs text-muted-foreground">Address</p><p>{r.address || "-"}</p></div>
          <div><p className="text-xs text-muted-foreground">Paid on</p><p>{fmtDate(r.payment_date)} {(r.payment_time || "").slice(0, 5)} IST</p></div>
          <div><p className="text-xs text-muted-foreground">Balance after this payment</p>
            <p className={`font-semibold tabular-nums ${result.outstanding_after > 0 ? "text-destructive" : "text-[hsl(var(--success))]"}`} data-testid="portal-success-balance">{fmtINR(result.outstanding_after)}</p></div>
          <div className="col-span-2 border-t pt-3">
            <p className="text-xs text-muted-foreground mb-1">Confirmation</p>
            <NotificationStatus code={code} token={result.token} />
          </div>
        </CardContent>
      </Card>

      {result.receipt_visible === false ? (
        /* Phase 169: the municipality has switched receipt viewing off for citizens — the payment is confirmed, the document is withheld */
        <>
          <Alert className="border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5" data-testid="portal-receipt-hidden-notice">
            <EyeOff className="h-4 w-4 text-[hsl(var(--warning))]" aria-hidden="true" />
            <AlertTitle className="text-sm">Receipt not available for viewing</AlertTitle>
            <AlertDescription className="text-xs text-muted-foreground">{result.receipt_hidden_message || "Receipt viewing is currently turned off by the municipality. Your payment is recorded and the receipt is kept safely on file."}</AlertDescription>
          </Alert>
          <div className="grid grid-cols-1 gap-2">
            <Button size="lg" variant="outline" className="h-12" onClick={onNew} data-testid="portal-pay-another-button">
              <Search className="h-4 w-4 mr-2" /> Pay for another property
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">Keep the receipt number safe — it is your proof of payment.</p>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Button size="lg" className="h-12" onClick={download} disabled={downloading} data-testid="citizen-receipt-download-button">
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Download receipt (PDF)
            </Button>
            <Button size="lg" variant="outline" className="h-12" asChild data-testid="portal-view-receipt-link">
              <Link to={`/${code}/pay/receipt/${result.token}`}><Printer className="h-4 w-4 mr-2" /> View / print receipt</Link>
            </Button>
            <Button size="lg" variant="ghost" className="h-12" onClick={onNew} data-testid="portal-pay-another-button">
              <Search className="h-4 w-4 mr-2" /> Pay for another property
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">Keep the receipt number safe. The receipt link was also sent to your mobile.</p>
        </>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------------------
// Page
// ------------------------------------------------------------------------------------------------
export default function CitizenPay() {
  const { code } = useParams();
  const [searchParams] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [cfgError, setCfgError] = useState("");
  const [step, setStep] = useState("search");
  const [propId, setPropId] = useState(searchParams.get("property") || "");
  const [lastSearch, setLastSearch] = useState(null);
  const [checkout, setCheckout] = useState(null);   // {order, property, payer}
  const [result, setResult] = useState(null);
  // Phase 208: `?pin=` deep link (printed as a QR on demand slips / notices / receipts) — resolve the PIN
  // to a property and land straight on the dues step; fall back to a pre-filled search otherwise.
  const pinParam = (searchParams.get("pin") || "").trim();
  const [pinResolving, setPinResolving] = useState(Boolean(pinParam && !searchParams.get("property")));

  useEffect(() => {
    api.get(`/t/${code}/public/pay/config`).then((r) => setConfig(r.data)).catch((e) => setCfgError(errMsg(e)));
  }, [code]);
  useEffect(() => { if (propId && step === "search") setStep("property"); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    if (!pinParam || searchParams.get("property")) return undefined;
    let alive = true;
    (async () => {
      const seed = { by: "pin", q: pinParam };
      try {
        const r = await api.get(`/t/${code}/public/pay/search`, { params: { by: "pin", q: pinParam } });
        const items = r.data?.items || [];
        const exact = items.find((p) => String(p.pin || "").trim().toLowerCase() === pinParam.toLowerCase());
        const hit = exact || (items.length === 1 ? items[0] : null);
        if (!alive) return;
        setLastSearch(seed);
        if (hit) { setPropId(hit.id); setStep("property"); }
        else if (items.length === 0) toast.info(`No property found for PIN ${pinParam} — try another search below`);
      } catch (e) {
        if (alive) { setLastSearch(seed); toast.error(errMsg(e)); }
      } finally {
        if (alive) setPinResolving(false);
      }
    })();
    return () => { alive = false; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [code, pinParam]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const muni = config?.municipality;
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="citizen-pay-page">
      <PortalHeader muni={muni} code={code} />
      <section className="civic-mist border-b">
        <div className="max-w-5xl mx-auto px-4 py-6 sm:py-8">
          <p className="inline-flex items-center gap-2 text-xs font-medium bg-card border rounded-full px-3 py-1 mb-3">
            <Landmark className="h-3.5 w-3.5" /> {muni?.name || "Municipality"}{muni?.state ? ` · ${muni.state}` : ""}
          </p>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading leading-tight">Pay your property tax online</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1.5 max-w-2xl">
            Find your property, see exactly what is due, and pay the full amount or a part of it. Your receipt is sent by SMS and WhatsApp instantly.
          </p>
          <div className="mt-4"><Stepper current={step} /></div>
        </div>
      </section>

      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
        {cfgError ? (
          <Card className="shadow-sm"><CardContent className="p-6 text-center space-y-3" data-testid="portal-config-error">
            <p className="font-medium">This payment portal is not available</p>
            <p className="text-sm text-muted-foreground">{cfgError}</p>
            <Button variant="outline" asChild><Link to="/pay">Choose a municipality</Link></Button>
          </CardContent></Card>
        ) : !config || pinResolving ? (
          <div className="space-y-3" data-testid={pinResolving ? "portal-pin-resolving" : "portal-config-skeleton"}>
            {pinResolving && (
              <p className="text-sm text-muted-foreground inline-flex items-center gap-2" data-testid="portal-pin-resolving-text">
                <Loader2 className="h-4 w-4 animate-spin" /> Looking up property <span className="font-mono font-medium text-foreground">{pinParam}</span>…
              </p>
            )}
            <Skeleton className="h-44 w-full rounded-xl" /><Skeleton className="h-20 w-full rounded-xl" />
          </div>
        ) : step === "search" ? (
          <SearchStep code={code} config={config} initial={lastSearch}
            onPick={(p, s) => { setLastSearch(s); setPropId(p.id); setStep("property"); }} />
        ) : step === "property" ? (
          <PropertyStep code={code} propId={propId} gatewayMode={config.gateway_mode}
            onBack={() => { setStep("search"); }}
            onProceed={(c) => { setCheckout(c); setStep("pay"); }} />
        ) : step === "pay" ? (
          <PayStep code={code} order={checkout.order} property={checkout.property} payer={checkout.payer}
            onBack={() => setStep("property")}
            onDone={(r) => { setResult(r); setStep("done"); toast.success(`Payment successful • Receipt ${r.receipt_number}`); }} />
        ) : (
          <DoneStep code={code} result={result} onNew={() => { setResult(null); setCheckout(null); setPropId(""); setLastSearch(null); setStep("search"); }} />
        )}
      </main>

      <footer className="border-t py-5 text-center text-xs text-muted-foreground px-4">
        <p className="inline-flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> Official payment portal of {muni?.name || "the municipality"}. Payments are recorded instantly in municipal records.</p>
        {muni?.helpline && <p className="mt-1">Helpline: {muni.helpline}</p>}
      </footer>
    </div>
  );
}

/** `/pay` — pick a municipality first, then go to its portal. */
export function PayMunicipalityPicker() {
  const [munis, setMunis] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => setMunis([])); }, []);
  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="pay-picker-page">
      <PortalHeader muni={null} code={null} />
      <section className="civic-mist border-b">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-heading">Pay property tax online</h1>
          <p className="text-sm sm:text-base text-muted-foreground mt-1.5">Choose your municipality to continue. No login or registration is needed.</p>
        </div>
      </section>
      <main className="flex-1 max-w-5xl w-full mx-auto px-4 py-6">
        {!munis ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
          : munis.length === 0 ? <p className="text-sm text-muted-foreground">No municipalities are live yet.</p>
          : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="pay-picker-list">
              {munis.map((m) => (
                <button key={m.code} type="button" onClick={() => navigate(`/${m.code}/pay`)}
                  className="text-left rounded-xl border bg-card p-4 hover:shadow-md hover:border-primary/40 focus-visible:ring-2 focus-visible:ring-ring transition-shadow duration-200 flex items-center justify-between gap-3 min-h-[72px]"
                  data-testid={`pay-picker-${m.code}`}>
                  <div>
                    <p className="font-semibold">{m.name}</p>
                    <p className="text-xs text-muted-foreground">{[m.district, m.state].filter(Boolean).join(", ")}</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              ))}
            </div>
          )}
      </main>
    </div>
  );
}
